import { fetchAllPages } from '@/lib/fetch-all-pages';
import { graphqlClient } from '@/lib/graphql-client';
import { dateOnlyOf, dateOnlyRangeClause } from '@/lib/utils/custom-date/custom-date';
import type { HrTermWorkDays } from '@/modules/time-attendance/attendance/services/attendance.service';
import {
  buildGetHolidaysForCalendarQuery,
  buildGetHrTermsForCalendarQuery,
  buildGetLeaveRequestsForCalendarQuery,
} from '../graphql/queries';
import {
  GetLeaveCalendarParams,
  LeaveCalendarData,
  LeaveCalendarEmployeeGroup,
  LeaveCalendarHolidayEvent,
} from '../types/leave-calendar.types';

/** Rows per request while the calendar reads its range. */
const CALENDAR_PAGE_SIZE = 500;

interface LeaveRequestForCalendar {
  ItemId: string;
  Title: string;
  UserId: string;
  DisplayName?: string;
  ProfileImageId?: string;
  LeaveCategory?: string;
  LeaveCategoryId?: string;
  LeaveType?: string;
  StartDate: string;
  EndDate: string;
  Status: string;
  IsDocumentSendByDr?: boolean;
  CreatedDate?: string;
  Timezone?: string;
}

/** Earliest start first, like the old calendar's `OrderBy(StartDateUTC)`. Each list is fetched whole
 * (one page per range), so sorting the returned items is enough; by date part, so old rows stored at
 * a non-midnight instant still fall on their day. */
const sortByStartDateAsc = <T extends { StartDate: string }>(items: T[]): T[] =>
  [...items].sort((a, b) => dateOnlyOf(a.StartDate).localeCompare(dateOnlyOf(b.StartDate)));

/**
 * Holidays on the picked days. `Holiday.StartDate` is date only (the day at 00:00:00 UTC), so it is in
 * range when it is on or after the first day and before the day after the last.
 */
const buildHolidayFilter = (startDate: string, endDate: string) => {
  if (!startDate || !endDate) return undefined;
  return { StartDate: dateOnlyRangeClause(startDate, endDate) };
};

/**
 * Leaves overlapping the picked days. `StartDate` / `EndDate` are date only and both inclusive, so a
 * leave overlaps when it starts before the day after the last picked day and ends on or after the first.
 */
const buildLeaveFilter = (
  startDate: string,
  endDate: string,
  status?: string,
  departmentId?: string | { $in: string[] },
  leaveCategoryId?: string
) => {
  const statusFilter = {
    ...(status ? { Status: status } : {}),
    ...(leaveCategoryId ? { LeaveCategoryId: leaveCategoryId } : {}),
    ...(departmentId ? { DepartmentId: departmentId } : {}),
  };
  const pickedDays = dateOnlyRangeClause(startDate, endDate);
  if (!startDate || !endDate || !pickedDays) return statusFilter;
  return {
    StartDate: { $lt: pickedDays.$lt },
    EndDate: { $gte: pickedDays.$gte },
    ...statusFilter,
  };
};

/**
 * Fetches LeaveRequests + Holidays for the given date range and groups them
 * per employee client-side (holidays once, beside the groups), mirroring the real LeaveCalendarResponseModel
 * shape as closely as flat GraphQL queries allow (see types file for the
 * fields that can't be faithfully reproduced this way).
 */
export const getLeaveCalendarData = async (
  params: GetLeaveCalendarParams
): Promise<LeaveCalendarData> => {
  const { startDate, endDate, status, departmentId, leaveCategoryId } = params;

  const leaveFilter = buildLeaveFilter(startDate, endDate, status, departmentId, leaveCategoryId);
  const holidayFilter = buildHolidayFilter(startDate, endDate);
  // A department-scoped user who may see no department sees no leave (holidays still show).
  const noDepartment = typeof departmentId === 'object' && departmentId.$in.length === 0;

  // Every leave and holiday of the range: the calendar groups them, so a missing page would drop some.
  const [leaveItems, holidayItems] = await Promise.all([
    noDepartment
      ? Promise.resolve([] as LeaveRequestForCalendar[])
      : fetchAllPages(async (pageNo, pageSize) => {
          const result = (await graphqlClient.query({
            query: buildGetLeaveRequestsForCalendarQuery(leaveFilter, pageNo, pageSize),
          })) as { LeaveRequests: { items: LeaveRequestForCalendar[]; totalCount: number } };
          return result.LeaveRequests;
        }, CALENDAR_PAGE_SIZE),
    // No readable range (a cleared date picker) means no holidays to look up.
    holidayFilter
      ? fetchAllPages(async (pageNo, pageSize) => {
          const result = (await graphqlClient.query({
            query: buildGetHolidaysForCalendarQuery(holidayFilter, pageNo, pageSize),
          })) as { Holidays: { items: LeaveCalendarHolidayEvent[]; totalCount: number } };
          return result.Holidays;
        }, CALENDAR_PAGE_SIZE)
      : Promise.resolve([] as LeaveCalendarHolidayEvent[]),
  ]);

  const sortedLeaveItems = sortByStartDateAsc(leaveItems);
  const holidayEvents = sortByStartDateAsc(holidayItems).map((holiday) => ({
    ItemId: holiday.ItemId,
    Name: holiday.Name,
    StartDate: holiday.StartDate,
  }));

  const groups = new Map<string, LeaveCalendarEmployeeGroup>();

  for (const leaveRequest of sortedLeaveItems) {
    const key = leaveRequest.UserId;
    let group = groups.get(key);
    if (!group) {
      group = {
        ItemId: key,
        DisplayName: leaveRequest.DisplayName ?? '',
        UserProfileImageUrl: leaveRequest.ProfileImageId,
        ProfileImageId: leaveRequest.ProfileImageId,
        LeaveEvents: [],
      };
      groups.set(key, group);
    }

    group.LeaveEvents.push({
      ItemId: leaveRequest.ItemId,
      Title: leaveRequest.Title,
      LeaveCategory: leaveRequest.LeaveCategory,
      LeaveCategoryId: leaveRequest.LeaveCategoryId,
      LeaveType: leaveRequest.LeaveType,
      Status: leaveRequest.Status,
      StartDate: leaveRequest.StartDate,
      EndDate: leaveRequest.EndDate,
      CreateDate: leaveRequest.CreatedDate,
      Timezone: leaveRequest.Timezone,
      IsDocumentSendByDr: leaveRequest.IsDocumentSendByDr,
    });
  }

  return { employees: Array.from(groups.values()), holidays: holidayEvents };
};

/** An employee's HR terms with their work days, by IAM user id. */
export type HrTermsByUser = Record<string, HrTermWorkDays[]>;

/**
 * The HR terms of the given people in one query (a page of the calendar), so the grid can shade each
 * person's own weekend: a day their term schedules no hours on.
 */
export const getHrTermsOfUsers = async (userIds: string[]): Promise<HrTermsByUser> => {
  if (userIds.length === 0) return {};
  const terms = await fetchAllPages(async (pageNo, pageSize) => {
    const result = (await graphqlClient.query({
      query: buildGetHrTermsForCalendarQuery({ UserId: { $in: userIds } }, pageNo, pageSize),
    })) as { HrProfiles: { items: (HrTermWorkDays & { UserId: string })[]; totalCount: number } };
    return result.HrProfiles;
  }, CALENDAR_PAGE_SIZE);
  const byUser: HrTermsByUser = {};
  for (const term of terms) (byUser[term.UserId] ??= []).push(term);
  return byUser;
};
